import { Heading } from '@app/app/_components/Heading/Heading';
import { Select } from '@app/app/_components/Input/Select/Select';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { ProductDataTerritories } from '@app/domain/model/eshop/transport/ProductDataTerritories';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import Link from 'next/link';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    mtplTerritories: ProductDataTerritories;
    formState: FormState<PurchaseTransportInsuranceResult>;
}

export function TerritoryPanel({ mtplTerritories, formState }: Props) {
    const t = useTranslations();
    const parameters = useAppParameters();

    const territoryError = formState.violations?.territory;

    const territoriesOptions = useMemo(() => {
        if (!mtplTerritories.territories || mtplTerritories.territories.length === 0) {
            return [];
        }
        return mtplTerritories.territories.map(territory => ({
            label: territory.name,
            value: territory.code,
        }));
    }, [mtplTerritories.territories]);

    const getInitialTerritory = () => {
        const savedTerritory = formState.data?.formData.territory;
        if (savedTerritory) {
            return savedTerritory;
        }
        return mtplTerritories.defaultCode || '';
    };

    return (
        <div className="flex flex-col gap-4 px-6">
            <div className="flex flex-row items-center gap-2">
                <Heading level="h3">{t('eshop.transport.territory')}</Heading>
                <InformationPopover
                    content={t.rich('eshop.transport.territoryTooltip', {
                        link: chunks => (
                            <Link
                                className="text-primary-500 underline"
                                target="_blank"
                                href={parameters.eshopInfoUrls.greenCourseInfo}>
                                {chunks}
                            </Link>
                        ),
                    })}>
                    <QuestionCircleIcon className="size-5 text-neutral-500" />
                </InformationPopover>
            </div>
            {territoriesOptions.length > 0 && (
                <Select
                    options={territoriesOptions}
                    name="territory"
                    placeholder={t('eshop.general.select')}
                    defaultValue={getInitialTerritory()}
                    error={territoryError}
                />
            )}
        </div>
    );
}
